"use client";

import { useMutation, useQuery } from "@apollo/client";
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE } from "@constants/global";
import {
  Award,
  CalendarCheck2,
  CalendarX2,
  CheckCircle2,
  ClockAlert,
  Download,
  ExternalLink,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { gqlUser } from "gql";
import type {
  IAddUserCertificationInput,
  IAddUserCertificationRes,
  IDeleteUserCertificationInput,
  IDeleteUserCertificationRes,
  IUpdateUserCertificationInput,
  IUpdateUserCertificationRes,
} from "gql/User/mutations";
import {
  Card,
  DatePickerV2,
  FileUploaderV2,
  type FileUploaderV2File,
  Input,
  LiquidPill,
  LiquidPillDropdown,
  type LiquidPillDropdownItem,
  Modal,
  ModalButtons,
  PageEmptyStateV2,
  PageLoadingStateV2,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/v2";
import { ECertificationType, ETypeCertification } from "types/User.types";
import { UploadPair } from "types/Multimedia.type";
import { getQueryOperator } from "utils/helpers";
import { CERTIFICATION_FORM_ID } from "components/ui/v2/shared/bundle/profile/CertificationForm";
import { ResumeTabHeaderV2 } from "./ResumeTabHeaderV2";

const emptyUploadPair: UploadPair = { name: "", base64: "" };

function getCertificationStatus(expiracyDate?: Date) {
  if (!expiracyDate) return "active";
  const daysUntilExpiry = moment(expiracyDate).diff(moment(), "days");
  if (daysUntilExpiry < 0) return "expired";
  if (daysUntilExpiry <= 30) return "expiring-soon";
  return "active";
}

function statusIcon(status: string) {
  if (status === "expired") return <XCircle className="h-3 w-3" />;
  if (status === "expiring-soon") return <ClockAlert className="h-3 w-3" />;
  return <CheckCircle2 className="h-3 w-3" />;
}

function statusVariant(status: string) {
  if (status === "expired") return "rose" as const;
  if (status === "expiring-soon") return "gold" as const;
  return "emerald" as const;
}

function CertificationCardV2({ certification, onEdit }: { certification: any; onEdit: (certification: any) => void }) {
  const { t } = useTranslation("common");
  const { id, name, type, expiracyDate, certificationUrl, url, emitDate, certificationType } = certification;
  const status = getCertificationStatus(expiracyDate);

  const [deleteUserCertification] = useMutation<
    { deleteUserCertification: IDeleteUserCertificationRes },
    { deleteUserCertificationInput: IDeleteUserCertificationInput }
  >(gqlUser.mutations.DELETE_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
  });

  const handleDownload = (downloadUrl: string) => window.open(downloadUrl, "_blank");
  const handleDelete = () => {
    toast.promise(
      deleteUserCertification({ variables: { deleteUserCertificationInput: { id } } }),
      {
        loading: t("Eliminando certificación"),
        success: t("Certificación eliminada"),
        error: t("Error al eliminar certificación"),
      }
    );
  };

  const actionItems = useMemo<LiquidPillDropdownItem[]>(() => {
    const items: LiquidPillDropdownItem[] = [
      { value: "edit", label: t("Editar"), icon: Pencil, variant: "indigo" },
      { value: "delete", label: t("Eliminar"), icon: Trash2, variant: "rose" },
    ];
    if (url) items.unshift({ value: "download", label: t("Descargar"), icon: Download, variant: "sky" });
    if (certificationUrl) items.unshift({ value: "view", label: t("Ver certificación"), icon: ExternalLink, variant: "indigo" });
    return items;
  }, [certificationUrl, t, url]);

  const handleAction = (value: string) => {
    if (value === "download" && url) handleDownload(url);
    if (value === "view" && certificationUrl) handleDownload(certificationUrl);
    if (value === "edit") onEdit(certification);
    if (value === "delete") handleDelete();
  };

  return (
    <Card size="full" variant="default" padding="none" shadow="subtle" fullHeight entranceAnimation>
      <article className="flex h-full min-h-[150px] flex-col overflow-hidden">
        <div className="flex flex-1 flex-col p-3 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Award className="mt-1 h-5 w-5 shrink-0 text-indigo-500 dark:text-indigo-300" />
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-base font-semibold leading-tight text-slate-950 dark:text-white">
                  {name}
                </h3>
                <p className="mt-1 text-xs font-medium text-slate-500 dark:text-zinc-400">{type}</p>
              </div>
            </div>

            <LiquidPillDropdown
              items={actionItems}
              onSelect={handleAction}
              icon={<MoreHorizontal className="h-4 w-4" />}
              variant="slate"
              size="sm"
              collapsedShape="circle"
              reactive
              aria-label={t("Opciones")}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
          <LiquidPill
            as="span"
            variant={statusVariant(status)}
            appearance="tinted"
            shadow="neutral"
            size="xs"
            icon={statusIcon(status)}
            text={
              status === "expired"
                ? t("Expirado")
                : status === "expiring-soon"
                  ? t("Por vencer")
                  : t("Vigente")
            }
          />
          <LiquidPill
            as="span"
            variant="indigo"
            appearance="tinted"
            shadow="neutral"
            size="xs"
            text={certificationType || ETypeCertification.CERTIFICATION}
          />
          </div>

          <div className="mb-2.5 mt-3 flex flex-wrap items-center justify-center gap-1.5 text-[11px] text-slate-500 dark:text-zinc-400">
            {emitDate ? (
              <>
                <CalendarCheck2 className="h-3 w-3 text-slate-400" />
                <span>{t("Emitida el")} <span className="font-medium text-slate-600 dark:text-zinc-300">{moment(emitDate).format("DD/MM/YYYY")}</span></span>
              </>
            ) : null}
            {emitDate && expiracyDate ? <span className="text-slate-300 dark:text-zinc-700">·</span> : null}
            {expiracyDate ? (
              <>
                <CalendarX2 className="h-3 w-3 text-slate-400" />
                <span>{t("Válida hasta")} <span className="font-medium text-slate-600 dark:text-zinc-300">{moment(expiracyDate).format("DD/MM/YYYY")}</span></span>
              </>
            ) : null}
          </div>

          {(() => {
            const needsRenew =
              status === "expired" || status === "expiring-soon";
            const showFooter = Boolean(url || certificationUrl || needsRenew);
            if (!showFooter) return null;
            const cols =
              [url, certificationUrl, needsRenew].filter(Boolean).length > 1
                ? "grid-cols-2"
                : "grid-cols-1";
            return (
              <div className="mt-auto border-t border-slate-200/70 pt-2.5 dark:border-zinc-800/80">
                <div className={`grid gap-2 ${cols}`}>
                  {needsRenew ? (
                    <LiquidPill
                      as="button"
                      type="button"
                      variant="gold"
                      appearance="tinted"
                      shadow="neutral"
                      size="card-cta"
                      icon={<RefreshCw className="h-3.5 w-3.5" />}
                      text={t("Renovar")}
                      aria-label={t("Renovar")}
                      onClick={() => onEdit(certification)}
                    />
                  ) : null}
                  {url ? (
                    <LiquidPill
                      as="button"
                      type="button"
                      variant="sky"
                      appearance="tinted"
                      shadow="neutral"
                      size="card-cta"
                      icon={<Download className="h-3.5 w-3.5" />}
                      text={t("Descargar")}
                      onClick={() => handleDownload(url)}
                    />
                  ) : null}
                  {certificationUrl ? (
                    <LiquidPill
                      as="button"
                      type="button"
                      variant="indigo"
                      appearance="tinted"
                      shadow="neutral"
                      size="card-cta"
                      icon={<ExternalLink className="h-3.5 w-3.5" />}
                      text={t("Ver certificación")}
                      onClick={() => handleDownload(certificationUrl)}
                    />
                  ) : null}
                </div>
              </div>
            );
          })()}
        </div>
      </article>
    </Card>
  );
}

function CertificationFormContentV2({
  certification,
  onClose,
  hideFooter = false,
  onFooterChange,
  formId = CERTIFICATION_FORM_ID,
}: {
  certification?: any;
  onClose: () => void;
  hideFooter?: boolean;
  onFooterChange?: (footer: ReactNode) => void;
  formId?: string;
}) {
  const onFooterChangeRef = useRef(onFooterChange);
  onFooterChangeRef.current = onFooterChange;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { t } = useTranslation("common");
  const [file, setFile] = useState<UploadPair>(emptyUploadPair);
  const [certImage, setCertImage] = useState<FileUploaderV2File | null>(null);

  const [addUserCertification, { loading: addCertLoading }] = useMutation<
    { addUserCertification: IAddUserCertificationRes },
    { addUserCertificationInput: IAddUserCertificationInput }
  >(gqlUser.mutations.ADD_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
    awaitRefetchQueries: true,
    onCompleted: () => onCloseRef.current?.(),
  });

  const [updateUserCertification, { loading: updateLoading }] = useMutation<
    { updateUserCertification: IUpdateUserCertificationRes },
    { updateUserCertificationInput: IUpdateUserCertificationInput }
  >(gqlUser.mutations.UPDATE_USER_CERTIFICATION, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_MY_SPACE)],
    awaitRefetchQueries: true,
    onCompleted: () => onCloseRef.current?.(),
  });

  const { handleSubmit, control, formState: { errors } } = useForm<IAddUserCertificationInput>({
    defaultValues: {
      name: certification?.name || "",
      type: certification?.type || ECertificationType.OTHER,
      certificationUrl: certification?.certificationUrl || "",
      certificationType: certification?.certificationType || ETypeCertification.CERTIFICATION,
    },
  });

  const handleImageChange = (next: FileUploaderV2File | null) => {
    setCertImage(next);
    if (!next?.file) {
      setFile(emptyUploadPair);
      return;
    }
    if (next.file.size > MAX_FILE_SIZE) {
      toast.error(`${t("El archivo es muy pesado. El tamaño máximo es de")} ${MAX_FILE_SIZE / 1000000} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(next.file);
    reader.onloadend = () => setFile({ base64: reader.result as string, name: next.name });
  };

  const onSubmit = (data: IAddUserCertificationInput) => {
    if (!certification) {
      toast.promise(addUserCertification({ variables: { addUserCertificationInput: { ...data, base64Certification: file.base64 } } }), {
        loading: t("Agregando certificación"),
        success: t("Certificación agregada"),
        error: t("Error al agregar certificación"),
      });
      return;
    }
    toast.promise(updateUserCertification({ variables: { updateUserCertificationInput: { ...data, base64Certification: file.base64, id: certification.id } } }), {
      loading: t("Actualizando certificación"),
      success: t("Certificación actualizada"),
      error: t("Error al actualizar certificación"),
    });
  };

  const originOptions = Object.keys(ECertificationType).map((key) => ({ label: (ECertificationType as any)[key], value: (ECertificationType as any)[key] }));
  const certificationTypeOptions = [
    { label: t("Certificación"), value: ETypeCertification.CERTIFICATION },
    { label: t("Curso"), value: ETypeCertification.COURSE },
    { label: t("Acreditación"), value: ETypeCertification.ACREDITATION },
    { label: t("Credencial"), value: ETypeCertification.CREDENTIAL },
  ];
  const submitLabel = certification ? t("Guardar") : t("Agregar");

  useLayoutEffect(() => {
    const publish = onFooterChangeRef.current;
    if (!hideFooter || !publish) return;

    publish(
      <ModalButtons
        compact
        className="!pt-0"
        onCancel={() => onCloseRef.current?.()}
        loading={addCertLoading || updateLoading}
        submitLabel={submitLabel}
        cancelLabel={t("Cancelar")}
        submitButtonProps={{ form: formId, type: "submit" }}
      />
    );
    // Do not depend on `onClose` ? a new function identity each render
    // would republish the footer forever (Maximum update depth exceeded).
  }, [
    addCertLoading,
    formId,
    hideFooter,
    submitLabel,
    t,
    updateLoading,
  ]);

  useEffect(() => {
    return () => {
      onFooterChangeRef.current?.(null);
    };
  }, []);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Controller
        name="name"
        control={control}
        rules={{ required: true, maxLength: 100 }}
        render={({ field }) => (
          <Input
            {...field}
            label={t("Nombre de la certificación")}
            leftIcon={<Award className="h-4 w-4" />}
            maxLength={100}
            placeholder={t("CERTIFICATION_PLACEHOLDER")}
            error={errors.name ? t("Ingresa el nombre de la certificación") : undefined}
          />
        )}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <Controller
          name="expiracyDate"
          control={control}
          defaultValue={certification?.expiracyDate ? moment(certification.expiracyDate).toDate() : undefined}
          render={({ field }) => (
            <DatePickerV2 label={t("Fecha expiración")} value={field.value ?? null} onChange={field.onChange} placeholder={t("Solo si aplica")} />
          )}
        />
        <Controller
          name="emitDate"
          control={control}
          defaultValue={certification?.emitDate ? moment(certification.emitDate).toDate() : undefined}
          render={({ field }) => (
            <DatePickerV2 label={t("Fecha emisión")} value={field.value ?? null} onChange={field.onChange} placeholder={t("Solo si aplica")} />
          )}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Controller
          name="certificationType"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger label={t("Tipo")} floating={Boolean(field.value)}>
                <SelectValue placeholder={t("Seleccione una opción")} />
              </SelectTrigger>
              <SelectContent>
                {certificationTypeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        />
        <Controller
          name="type"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger label={t("Origen de la certificación")} floating={Boolean(field.value)}>
                <SelectValue placeholder={t("Seleccione una opción")} />
              </SelectTrigger>
              <SelectContent>
                {originOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-slate-600 dark:text-zinc-400">{t("Certificación")}</p>
        <FileUploaderV2
          value={certImage}
          onChange={handleImageChange}
          accept={ALLOWED_EXTENSIONS}
          title={t("Arrastra y suelta tu certificación")}
          hint={t("PDF o imágenes · hasta 50MB")}
          aria-label={t("Subir certificación")}
        />
      </div>

      {!hideFooter ? (
        <ModalButtons
          onCancel={() => onCloseRef.current?.()}
          loading={addCertLoading || updateLoading}
          submitLabel={submitLabel}
          cancelLabel={t("Cancelar")}
        />
      ) : null}
    </form>
  );
}

export function ResumeCertificationsTabV2() {
  const { t } = useTranslation("common");
  const [selectedCertification, setSelectedCertification] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [certFooter, setCertFooter] = useState<ReactNode>(null);
  const { data, loading } = useQuery(gqlUser.queries.GET_MY_SPACE, { fetchPolicy: "network-only" });

  const certifications = data?.mySpace?.userCertifications ?? [];

  const handleOpenCreate = () => {
    setSelectedCertification(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (certification: any) => {
    setSelectedCertification(certification);
    setModalOpen(true);
  };

  return (
    <div className="grid gap-4">
      <ResumeTabHeaderV2
        title={t("Certificaciones")}
        description={t("Agrega certificaciones para completar tu resumen profesional")}
        action={
          <LiquidPill
            as="button"
            type="button"
            variant="indigo"
            appearance="tinted"
            shadow="neutral"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            text={t("Agregar certificación")}
            onClick={handleOpenCreate}
            aria-label={t("Agregar certificación")}
          />
        }
      />

      {loading ? (
        <PageLoadingStateV2 title={t("Cargando")} description={t("Certificaciones")} />
      ) : certifications.length === 0 ? (
        <PageEmptyStateV2
          variant="empty"
          icon={<Award className="h-8 w-8" />}
          title={t("No tienes certificaciones cargadas")}
          description={t("Agrega certificaciones para completar tu resumen profesional")}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {certifications.map((certification: any) => (
            <CertificationCardV2 key={certification.id} certification={certification} onEdit={handleOpenEdit} />
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setCertFooter(null);
        }}
        title={selectedCertification ? t("Editar certificación") : t("Agregar certificaciones")}
        description={t("Agrega una certificación para que los usuarios puedan ver tu progreso en tu perfil")}
        size="md"
        panelClassName="max-h-[min(88vh,680px)]"
        footer={certFooter}
      >
        <CertificationFormContentV2
          certification={selectedCertification}
          onClose={() => {
            setModalOpen(false);
            setCertFooter(null);
          }}
          hideFooter
          onFooterChange={setCertFooter}
        />
      </Modal>
    </div>
  );
}
